import React, { useEffect, useState } from 'react';
import Breadcrumb from '../../../components/Breadcrumb';
import {
  createProject,
  updateProject,
  getProjectById,
} from '../../../axios/ProjectAxios';
import Swal from 'sweetalert2';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LoadingOverlay from '../../../components/LoadingOverlay';

export default function ProjectAdd() {
  const [breadcrumbItems, setBreadcrumbItems] = useState([
    { label: 'Dashboard', href: '/' },
    { label: 'Project', href: '/project' },
    { label: 'Create Project' },
  ]);

  const navigation = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    project_code: '',
    name: '',
    key_person_id: '',
    customer_id: '',
    description: '',
    contact: '',
    contact_date: '',
    contact_check: 'FALSE',
    wo: '',
    wo_date: '',
    wo_check: 'FALSE',
    value: '',
    budget: '',
    address1: '',
    address2: '',
    phone1: '',
    phone2: '',
    invoice: '',
    invoice_date: '',
    invoice_check: 'FALSE',
    compilation: '',
    compilation_date: '',
    compilation_check: 'FALSE',
    start: '',
    start_date: '',
    start_check: 'FALSE',
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Set breadcrumb based on the presence of 'id'
  useEffect(() => {
    if (id) {
      setBreadcrumbItems([
        { label: 'Dashboard', href: '/' },
        { label: 'Project', href: '/project' },
        { label: 'Update Project' },
      ]);
    }
  }, [id]);

  // Fetch project data if 'id' exists (for update)
  useEffect(() => {
    if (id) {
      setMessage('Fetching data...');
      setLoading(true);
      const fetchProjectData = async () => {
        try {
          const response = await getProjectById(id); // Get data by ID from backend
          console.log(response);
          const formattedData = {
            ...response,
            contact_date: response.contact_date
              ? response.contact_date.split('T')[0]
              : response.contact_date, // Hanya ambil tanggal (YYYY-MM-DD)
            wo_date: response.wo_date
              ? response.wo_date.split('T')[0]
              : response.wo_date, // Hanya ambil tanggal (YYYY-MM-DD)
            invoice_date: response.wo_date
              ? response.wo_date.split('T')[0]
              : response.wo_date,
            compilation_date: response.wo_date
              ? response.wo_date.split('T')[0]
              : response.wo_date,
            start_date: response.wo_date
              ? response.wo_date.split('T')[0]
              : response.wo_date,
          };
          setFormData(formattedData); // Mengisi form dengan data yang telah diformat
        } catch (error) {
          console.error('Error fetching project data:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchProjectData();
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    if (!form.checkValidity()) {
      form.classList.add('was-validated');
      return;
    }

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-primary mx-1', // Bootstrap primary button
        denyButton: 'btn btn-danger mx-1', // Bootstrap warning button
        cancelButton: 'btn btn-secondary mx-1', // Bootstrap secondary button
      },
      buttonsStyling: false,
    });

    try {
      const result = await swalWithBootstrapButtons.fire({
        title: 'Do you want to save the data?',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Save',
        cancelButtonText: `Don't save`,
      });

      if (result.isConfirmed) {
        try {
          setMessage('Saving data...');
          setLoading(true);
          if (id) {
            await updateProject(id, formData);
            swalWithBootstrapButtons.fire(
              'Saved!',
              'The project has been updated.',
              'success'
            );
          } else {
            console.log(formData);
            await createProject(formData);
            // Jika berhasil, tampilkan pesan sukses
            swalWithBootstrapButtons.fire(
              'Saved!',
              'The project has been created.',
              'success'
            );
          }

          navigation('/project');
        } catch (createProjectError) {
          // Jika ada kesalahan saat membuat project, tampilkan pesan kesalahan
          swalWithBootstrapButtons.fire({
            title: 'Error!',
            text: createProjectError.message,
            icon: 'error',
          });
        } finally {
          setLoading(false);
        }
      } else if (result.isDenied) {
        swalWithBootstrapButtons.fire('Changes are not saved', '', 'info');
      }
    } catch (error) {
      swalWithBootstrapButtons.fire({
        title: 'Error!',
        text: 'There was an error processing your request.',
        icon: 'error',
      });
      console.log(error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const formElements = Array.from(e.target.form.elements);
      const index = formElements.indexOf(e.target);
      formElements[index + 1]?.focus();
    }
  };
  const handleCancel = (event) => {
    event.preventDefault();

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-primary mx-1', // Bootstrap primary button
        denyButton: 'btn btn-danger mx-1', // Bootstrap warning button
        cancelButton: 'btn btn-secondary mx-1', // Bootstrap secondary button
      },
      buttonsStyling: false,
    });
    swalWithBootstrapButtons
      .fire({
        title: 'Are you sure?',
        text: 'All your input will be lost!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel it!',
        cancelButtonText: 'No, keep it',
      })
      .then((result) => {
        if (result.isConfirmed) {
          const form = document.querySelector('.needs-validation');
          form.reset();
          setFormData({
            project_code: '',
            name: '',
            key_person_id: '',
            customer_id: '',
            description: '',
            contact: '',
            contact_date: '',
            contact_check: 'FALSE',
            wo: '',
            wo_date: '',
            wo_check: 'FALSE',
            value: '',
            budget: '',
            address1: '',
            address2: '',
            phone1: '',
            phone2: '',
            invoice: '',
            invoice_date: '',
            invoice_check: 'FALSE',
            compilation: '',
            compilation_date: '',
            compilation_check: 'FALSE',
            start: '',
            start_date: '',
            start_check: 'FALSE',
          });
          form.classList.remove('was-validated');
        }
      });
  };

  return (
    <div>
      <LoadingOverlay show={loading} message={message} />
      <Breadcrumb items={breadcrumbItems}></Breadcrumb>
      <form
        className="needs-validation"
        noValidate
        autocomplete="off"
        onSubmit={handleSubmit}
      >
        <div className="row">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="validationAccCode" className="form-label">
                      Project Code
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      id="validationAccCode"
                      name="project_code"
                      value={formData.project_code}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      required
                    />
                    <div className="invalid-feedback">Cannot be empty.</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      id="validationName"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                      required
                    />
                    <div className="invalid-feedback">Cannot be empty.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label
                      htmlFor="validationKeyPersonId"
                      className="form-label"
                    >
                      Key Person Id
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      id="validationKeyPersonId"
                      name="key_person_id"
                      value={formData.key_person_id}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Customer Id</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      id="validationCustomerId"
                      name="customer_id"
                      value={formData.customer_id}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      id="validationDescription"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Contact</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="contact"
                      value={formData.contact}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Contact Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      name="contact_date"
                      value={formData.contact_date}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Contact Check</label>
                    <select
                      className="form-select form-select-sm"
                      aria-label="Small select example"
                      name="contact_check"
                      value={formData.contact_check}
                      onChange={handleChange}
                    >
                      <option value="FALSE">FALSE</option>
                      <option value="TRUE">TRUE</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">WO</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="wo"
                      value={formData.wo}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">WO Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      name="wo_date"
                      value={formData.wo_date}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">WO Check</label>
                    <select
                      className="form-select form-select-sm"
                      aria-label="Small select example"
                      name="wo_check"
                      value={formData.wo_check}
                      onChange={handleChange}
                    >
                      <option value="FALSE">FALSE</option>
                      <option value="TRUE">TRUE</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Value</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="value"
                      value={formData.value}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Budget</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Address 1</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="address1"
                      value={formData.address1}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Address 2</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="address2"
                      value={formData.address2}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Phone 1</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="phone1"
                      value={formData.phone1}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Phone 2</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="phone2"
                      value={formData.phone2}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Invoice</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="invoice"
                      value={formData.invoice}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Invoice Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      name="invoice_date"
                      value={formData.invoice_date}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Invoice Check</label>
                    <select
                      className="form-select form-select-sm"
                      aria-label="Small select example"
                      name="invoice_check"
                      value={formData.invoice_check}
                      onChange={handleChange}
                    >
                      <option value="FALSE">FALSE</option>
                      <option value="TRUE">TRUE</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Compilation</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="compilation"
                      value={formData.compilation}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Compilation Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      name="compilation_date"
                      value={formData.compilation_date}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Compilation Check</label>
                    <select
                      className="form-select form-select-sm"
                      aria-label="Small select example"
                      name="compilation_check"
                      value={formData.compilation_check}
                      onChange={handleChange}
                    >
                      <option value="FALSE">FALSE</option>
                      <option value="TRUE">TRUE</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Start</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="start"
                      value={formData.start}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Start Check</label>
                    <select
                      className="form-select form-select-sm"
                      aria-label="Small select example"
                      name="start_check"
                      value={formData.start_check}
                      onChange={handleChange}
                    >
                      <option value="FALSE">FALSE</option>
                      <option value="TRUE">TRUE</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="button-navigatsi">
            <div className="">
              <button type="submit" className="btn btn-primary">
                <i className="bi bi-send"></i>
                <span> Submit</span>
              </button>
            </div>
            <div className="">
              <button
                type="reset"
                className="btn btn-danger btn-cancel"
                onClick={handleCancel}
              >
                <i className="bi bi-x-circle"></i>
                <span> Cancel</span>
              </button>
            </div>
            <div className="">
              <Link type="button" className="btn btn-warning" to="/project">
                <i className="bi bi-arrow-left-circle"></i>
                <span> Back</span>
              </Link>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
